import { TCKEditor } from '~/components/common/CKEditorWrapper/ckeditor.types';
import { isEmailValid } from '~/utils/emails.utils';
import { getFullUrl, isValidUrl } from '~/utils/url.utils';
import { DomEventData, Element, Node, Range } from '@ckeditor/ckeditor5-engine';

export const getSelectedText = ({ editor }: { editor: TCKEditor }): string => {
    const selection = editor.model.document.selection;
    const range = selection.getFirstRange();

    let res = '';
    for (const item of range.getItems()) {
        res += (item as any)?.data || '';
    }
    return res;
};

export const getTextAndUrlOfSelectedLink = ({
    editor
}: {
    editor: TCKEditor;
}) => {
    const selection = editor.model.document.selection;
    const range = selection.getFirstRange();

    let text = '';
    let url = '';
    for (const item of range.getItems()) {
        text += (item as any)?.data || '';
        if (!url && item.hasAttribute('linkHref')) {
            url = item.getAttribute('linkHref') as string;
        }
    }
    return { text, url };
};

export const getAllLinksInEditor = ({
    editorInstance
}: {
    editorInstance: TCKEditor;
}): Array<{ text: string; url: string }> => {
    const nodes = getNodeDescendants({
        node: editorInstance.model.document.getRoot()
    });
    const links = [];
    for (const node of nodes) {
        if (node.hasAttribute('linkHref')) {
            links.push({
                text: (node as any).data,
                url: node.getAttribute('linkHref') as string
            });
        }
    }
    return links;
};

function getNodeDescendants({ node }: { node: Element }): Node[] {
    if (!node.childCount) {
        return [];
    }
    const nodes = [];
    for (const child of node.getChildren()) {
        nodes.push(child, ...getNodeDescendants({ node: child as Element }));
    }

    return nodes;
}

export const writeText = ({
    editor,
    text,
    insertLineBreak = false,
    isBold = false
}: {
    editor: TCKEditor;
    text: string;
    insertLineBreak?: boolean;
    isBold?: boolean;
}) => {
    editor.model.change(writer => {
        const textNode = writer.createText(text);
        editor.model.insertContent(textNode);
        if (isBold) {
            writer.setSelection(textNode, 'on');
            editor.execute('bold');
            writer.setSelection(textNode, 'after');
        }
        if (insertLineBreak) {
            editor.execute('enter');
        }
    });
};

export const setCursorToEndOfMessage = ({
    editor: { model }
}: {
    editor: TCKEditor;
}) => {
    const root = model.document.getRoot();
    const endPosition = model.createPositionAt(root, 'end');
    model.change(writer => {
        writer.setSelection(endPosition);
    });
};

export const setCursorToEndOfCurrentLine = ({
    editor
}: {
    editor: TCKEditor;
}) => {
    editor.model.change(writer => {
        const selection = editor.model.document.selection;
        const position = selection.getFirstPosition();
        const endPosition = editor.model.createPositionAt(
            position.parent,
            'end'
        );
        writer.setSelection(endPosition);
    });
};

export const insertLineBreak = ({ editor }: { editor: TCKEditor }) => {
    editor.execute('enter');
};

export const writeLink = ({
    editor,
    text,
    url
}: {
    editor: TCKEditor;
    text: string;
    url: string;
}) => {
    editor.model.change(writer => {
        const textNode = writer.createText(text);
        const selection = editor.model.insertContent(textNode);
        writer.setSelection(selection);
        const isEmail = isEmailValid(url);
        const finalUrl = isEmail ? `mailto:${url}` : getFullUrl(url);
        editor.execute('link', finalUrl, {
            target: '_blank',
            rel: 'noopener noreferrer'
        });

        writer.setSelection(editor.model.document.selection.getLastPosition());
    });
};

export const removeSelectedLink = ({ editor }: { editor: TCKEditor }) => {
    editor.execute('unlink');
};

export const selectLinkByTarget = ({
    editorInstance,
    viewTarget,
    target
}: {
    editorInstance: TCKEditor;
    viewTarget: DomEventData['target'];
    target: HTMLAnchorElement;
}) => {
    const paragraphModelNode = editorInstance.editing.mapper.toModelElement(
        viewTarget.parent as any
    );
    const linkNodes = findLinkInParagraphByTarget({
        paragraphModelNode,
        target
    });

    let linkRange;
    editorInstance.model.change(writer => {
        const ranges = linkNodes.map(link => writer.createRangeOn(link));
        linkRange = writer.createRange(
            ranges[0].start,
            ranges[ranges.length - 1].end
        );
    });

    const selectLink = () => {
        editorInstance.model.change(writer => {
            writer.setSelection(linkRange);
        });
    };

    if (isRangeSelected({ editorInstance, range: linkRange })) {
        // add a delay to ensure the link is re-selected
        setTimeout(() => {
            selectLink();
        }, 50);
    } else {
        selectLink();
    }
};

function findLinkInParagraphByTarget({
    paragraphModelNode,
    target
}: {
    paragraphModelNode: Element;
    target: HTMLAnchorElement;
}): Node[] {
    if (paragraphModelNode.childCount === 1) {
        return [paragraphModelNode.getChild(0)];
    }

    const targetText = target.textContent?.trim();

    let linkIndices = [];
    for (let i = 0; i < paragraphModelNode.childCount; i++) {
        const child = paragraphModelNode.getChild(i);
        if ((child as any).data?.trim() === targetText) {
            linkIndices.push(i);
        }
    }

    if (linkIndices.length === 0) {
        // this code is edge case - if a link is broken to multiple children because part of the link text has different style (e.g. bold/underline), then search for the children that their combined text is equal to the target text
        let startIndex, endIndex;
        for (let i = 0; i < paragraphModelNode.childCount; i++) {
            const child = paragraphModelNode.getChild(i);
            const childText = (child as any).data?.trim();
            if (targetText?.startsWith(childText)) {
                startIndex = i;
            } else if (targetText?.endsWith(childText)) {
                endIndex = i;
            }
        }
        if (startIndex !== undefined && endIndex !== undefined) {
            return Array.from({ length: endIndex - startIndex + 1 }, (_, i) =>
                paragraphModelNode.getChild(startIndex + i)
            );
        }
        return null;
    } else if (linkIndices.length === 1) {
        return [paragraphModelNode.getChild(linkIndices[0])];
    } else {
        // this code is edge case - if multiple links with the same text exist in the same paragraph, then compare by href

        if (!isValidUrl(target.getAttribute('href'))) {
            return [paragraphModelNode.getChild(linkIndices[0])];
        }

        linkIndices = [];
        for (let i = 0; i < paragraphModelNode.childCount; i++) {
            const child = paragraphModelNode.getChild(i);
            if (
                (child as any).data?.trim() === target.textContent?.trim() &&
                child.getAttribute('linkHref') === target.getAttribute('href')
            ) {
                linkIndices.push(i);
            }
        }

        if (linkIndices.length === 1) {
            return [paragraphModelNode.getChild(linkIndices[0])];
        }

        const targetIndex = [
            ...target.parentElement.querySelectorAll(':scope > a')
        ]
            .filter(
                anchor =>
                    anchor.textContent === target.textContent &&
                    anchor.getAttribute('href') === target.getAttribute('href')
            )
            .indexOf(target);
        return [paragraphModelNode.getChild(linkIndices[targetIndex])];
    }
}

function isRangeSelected({
    editorInstance,
    range
}: {
    editorInstance: TCKEditor;
    range: Range;
}) {
    let currentRange = editorInstance.model.document.selection.getFirstRange();
    return currentRange.isEqual(range);
}

export function setIsCKToolbarThreeDotsMenuOpen(open: boolean) {
    const container = document.querySelector('.ck-toolbar__grouped-dropdown');
    if (!container) {
        return;
    }
    const button = container.querySelector('button');
    const toolbar = container.querySelector('.ck-dropdown__panel');
    const isToolbarVisible = toolbar.classList.contains(
        'ck-dropdown__panel-visible'
    );
    const shouldClick = isToolbarVisible ? !open : open;

    if (shouldClick) {
        button.click();
    }
}

export function selectAllText(editorInstance: TCKEditor) {
    const editableElement = editorInstance.ui.getEditableElement();
    if (!editableElement) {
        return;
    }
    const range = document.createRange();
    range.selectNodeContents(editableElement);
    const selection = window.getSelection();
    if (!selection) {
        return;
    }
    selection.removeAllRanges();
    selection.addRange(range);
}

export function preventEnterLineBreaks(editor: TCKEditor) {
    // Override the enter command to do nothing for single-line like editor
    editor.commands.get('enter').execute = () => {};
    if (editor.commands.get('shiftEnter')) {
        editor.commands.get('shiftEnter').execute = () => {};
    }
}
